/**
 * Route guard for the commune-admin console (`docs/ADMIN.md` §1): reachable
 * only by a `commune_admin` membership of the `:insee` in the URL. Unlike
 * `routes/guards.tsx`'s `RequireFullAccess`, a volunteer who fails this
 * check is never redirected — they land on a refusal page, in place, so a
 * bookmarked or typed console URL never silently bounces to `/`.
 */
import { Navigate, useLocation, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuth } from '../../auth/AuthContext';
import { LoadingScreen } from '../../routes/guards';
import { AdminRefusalPage } from './pages/AdminRefusalPage';

import type { Membership, User } from '../../api/types';

export interface AdminGuardProps {
  children: ReactNode;
}

/**
 * `user`'s memberships, or none while auth is still loading.
 *
 * @param user - The signed-in user, or `undefined`.
 * @returns The membership rows.
 */
function membershipsOf(user: User | undefined): Membership[] {
  return user?.memberships ?? [];
}

/**
 * Whether `user` administers the commune identified by `insee`.
 *
 * @param user - The signed-in user, or `undefined` while auth is loading.
 * @param insee - The commune's INSEE code, from the route.
 * @returns `true` when a `commune_admin` membership matches `insee`.
 */
function isAdminOf(user: User | undefined, insee: string | undefined): boolean {
  if (!user || !insee) return false;
  return membershipsOf(user).some(
    (membership) => membership.commune.insee_code === insee && membership.role === 'commune_admin',
  );
}

/**
 * Gate the commune-admin console: full auth first (same checks as
 * `RequireFullAccess`), then a `commune_admin` membership of the route's
 * `:insee`.
 *
 * @param props - The guarded content (the admin layout and its routed page).
 * @returns The children, a login/onboarding/pending redirect, the refusal
 *   page, or a loading placeholder.
 */
export function RequireCommuneAdmin({ children }: AdminGuardProps) {
  const { status, user, profileComplete } = useAuth();
  const { insee } = useParams<{ insee: string }>();
  const location = useLocation();

  if (status === 'loading') {
    return <LoadingScreen />;
  }
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (!profileComplete) {
    return <Navigate to="/onboarding" replace />;
  }
  if (!user?.is_validated) {
    return <Navigate to="/pending" replace />;
  }
  if (!isAdminOf(user, insee)) {
    return <AdminRefusalPage />;
  }
  return children;
}

/**
 * The commune-admin console's own INSEE code, from the caller's
 * memberships — used to build the "Console admin" nav link so it never has
 * to be typed by hand.
 *
 * @param user - The signed-in user, or `undefined`.
 * @returns The first commune this user administers, or `undefined`.
 */
export function firstAdminCommuneInsee(user: User | undefined): string | undefined {
  return membershipsOf(user).find((membership) => membership.role === 'commune_admin')?.commune
    .insee_code;
}
